import React from 'react'
import Alert from '@mui/material/Alert'
import FormField from 'components/FormField'
import ErrorList from 'components/ErrorList'
import CheckboxSelectField from 'components/CheckboxSelectField'
import { AddJobForm, EditJobForm, NursePreferenceOption } from 'types/interfaces'
import AddJobError from 'errors/AddJobError'
import EditJobError from 'errors/EditJobError'
import { round } from 'lodash'
import { NursePaygradeStats } from 'types/interfaces'
import { EstimateCost } from 'types/interfaces'
import { Box, Link, MenuItem, Typography } from '@mui/material'
import SwitchField from './SwitchField'
import update from 'immutability-helper'
import { NURSE_PREFERENCE_LABEL_MAPPING, HYGIENIST_SUPPORT_LABEL_MAPPING } from 'types/constants'
import { NursePreference } from 'types/types'
import { Link as RouterLink } from 'react-router-dom'
import { practicePreferredNursesURL } from 'routes/urls'
import { usePractice } from 'contexts/PracticeProvider'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DateTime } from 'luxon'
import { floorDateTime } from 'logic/helpers'

type JobFormType = AddJobForm | EditJobForm
type JobError = AddJobError | EditJobError

interface JobFormProps {
  form: JobFormType
  error?: JobError
  updateForm: (name: string, value: any) => void
  setForm: (form: JobFormType) => void
  mode: 'add' | 'edit'
  paygradeStats?: NursePaygradeStats | null
  estimatedCost?: EstimateCost | null
  nursePreferenceOption?: NursePreferenceOption
  canRequestHygienist?: boolean
  canAddPreferredNurses?: boolean
  canEditBonusRate?: boolean
  defaultBonusRateLabel?: string
}

const JobForm: React.FC<JobFormProps> = ({ 
  form, 
  error, 
  updateForm, 
  setForm, 
  mode, 
  paygradeStats, 
  estimatedCost, 
  nursePreferenceOption,
  canRequestHygienist = true,
  canAddPreferredNurses = true,
  canEditBonusRate = false,
  defaultBonusRateLabel = '£20/hr',
}) => {
  const { practice } = usePractice()
  
  const isAddJobForm = (form: JobFormType): form is AddJobForm => {
    return mode === 'add'
  }

  const isAddJobError = (error?: JobError): error is AddJobError => {
    return mode === 'add'
  }

  const role = React.useMemo(() => form.hygienist_required ? 'hygienist' : 'nurse', [form.hygienist_required])

  const canRequestSpecialist = React.useMemo(() => {
    // TODO: update this once we decided to go live with specialist requests
    return false
  }, [])

  const canSelectNursePreference = React.useMemo(() => {
    // can select if there are more than one option
    return (nursePreferenceOption?.available_options?.length ?? 0) > 1
  }, [nursePreferenceOption])

  const maxWaitUntil = React.useMemo(() => {
    if (!nursePreferenceOption || !nursePreferenceOption.max_wait_until) return null
    return floorDateTime(DateTime.fromISO(nursePreferenceOption.max_wait_until))
  }, [nursePreferenceOption])

  const waitUntilErrorMessage = React.useMemo(() => {
    if (form.preferred_nurses_wait_until) {
      const waitUntil = DateTime.fromISO(form.preferred_nurses_wait_until)
      if (waitUntil < DateTime.now()) {
        return 'Cannot set a wait time in the past'
      }
      if (maxWaitUntil && waitUntil > maxWaitUntil) {
        return `Maximum wait time is ${maxWaitUntil?.toFormat('dd MMM, yyyy hh:mm a')}`
      }
    }
    return ''
  }, [form.preferred_nurses_wait_until, maxWaitUntil])

  const updateRole = React.useCallback((value: 'nurse' | 'hygienist') => {
    // specialist requirements are not applicable to hygienists.
    // set them to false if a hygienist is selected.
    const hygienistRequired = value === 'hygienist'
    if (hygienistRequired) {
      setForm(update(form, { 
        hygienist_required: { $set: true },
        sedation_required: { $set: false },
        radiography_required: { $set: false },
        implant_required: { $set: false },
        orthodontics_required: { $set: false },
        hygienist_support: { $set: 'none' },
      }))
    } else {
      setForm(update(form, { 
        hygienist_required: { $set: false },
        $unset: ['hygienist_support'],
      }))
    }
  }, [form, setForm])

  const updateNursePreference = React.useCallback((value: NursePreference) => {
    if (value === 'preferred_only') {
      setForm(update(form, { 
        nurse_preference: { $set: 'preferred_only' },
        $unset: ['preferred_nurses_wait_until'],
      }))
    } else if (value === 'preferred_first') {
      setForm(update(form, { 
        nurse_preference: { $set: 'preferred_first' },
        preferred_nurses_wait_until: { $set: maxWaitUntil?.toISO() ?? '' },
      }))
    } else {
      setForm(update(form, { 
        nurse_preference: { $set: 'any' },
        $unset: ['preferred_nurses_wait_until'],
      }))
    }
  }, [form, setForm, maxWaitUntil])

  React.useEffect(() => {
    if (!canSelectNursePreference) {
      updateNursePreference('any')
    }
  }, [canSelectNursePreference, updateNursePreference])
  
  return (
    <React.Fragment>
      {isAddJobForm(form) && isAddJobError(error) ? (
        <React.Fragment>
          <CheckboxSelectField 
            onChange={(value) => updateRole(value as 'nurse' | 'hygienist')}
            value={role}
            sx={{ flexDirection: 'row' }}
            options={[{
              value: 'nurse',
              label: 'Dental Nurse',
            }, {
              value: 'hygienist',
              label: canRequestHygienist ? 'Hygienist' : 'Hygienist (Coming soon)',
              disabled: !canRequestHygienist,
            }]} 
            errors={error?.hygienist_required}
          />
          <FormField
            name='date'
            label='Date'
            type='date'
            InputLabelProps={{
              shrink: true,
            }}
            onChange={(e) => updateForm('date', e.target.value)}
            value={form.date ?? ''}
            errors={error?.date}
          />
        </React.Fragment>
      ) : null}
      <FormField
        name='start_time'
        label='Start Time'
        type='time'
        onChange={(e) => updateForm('start_time', e.target.value)}
        value={form.start_time ?? ''}
        errors={error?.start_time}
        InputLabelProps={{
          shrink: true,
        }}
      />
      <FormField
        name='end_time'
        label='End Time'
        type='time'
        onChange={(e) => updateForm('end_time', e.target.value)}
        value={form.end_time ?? ''}
        errors={error?.end_time}
        InputLabelProps={{
          shrink: true,
        }}
      />
      <FormField
        name='lunch_break'
        label='Lunch Break (minutes)'
        type='number'
        onChange={(e) => updateForm('lunch_break', e.target.value)}
        value={form.lunch_break ?? ''}
        errors={error?.lunch_break}
      />
      <FormField
        name='description'
        label='Description'
        multiline
        rows={2}
        placeholder="Include information useful to the nurse, such as parking availability, time of lunch break etc."
        onChange={(e) => updateForm('description', e.target.value)}
        value={form.description ?? ''}
        errors={error?.description}
      />
      <FormField
        select
        name='parking'
        label='Parking'
        onChange={(e) => updateForm('parking', e.target.value)}
        value={form.parking ?? ''}
        errors={error?.parking}
      >
        <MenuItem value='not specified'>Not Specified</MenuItem>
        <MenuItem value='free parking available'>Free Parking Available</MenuItem>
        <MenuItem value='paid parking available'>Paid Parking Available</MenuItem>
        <MenuItem value='no parking available'>No Parking Available</MenuItem>
      </FormField>
      <FormField
        name='headcount'
        label={`Number of ${role === 'nurse' ? 'Nurses' : 'Hygienists'} Required`}
        type='number'
        onChange={(e) => updateForm('headcount', e.target.value)}
        value={form.headcount ?? 1}
        errors={error?.headcount}
      />
      {role === 'hygienist' ? (
        <FormField
          select
          name='hygienist_support'
          label='Will a dental nurse be provided for this hygienist booking?'
          onChange={(e) => updateForm('hygienist_support', e.target.value)}
          value={form.hygienist_support ?? 'none'}
          errors={error?.hygienist_support}
        >
          <MenuItem key='chairside' value='chairside'>{HYGIENIST_SUPPORT_LABEL_MAPPING['chairside']}</MenuItem>
          <MenuItem key='decontamination_only' value='decontamination_only'>{HYGIENIST_SUPPORT_LABEL_MAPPING['decontamination_only']}</MenuItem>
          <MenuItem key='none' value='none'>{HYGIENIST_SUPPORT_LABEL_MAPPING['none']}</MenuItem>
        </FormField>
      ) : null}
      {canEditBonusRate ? (
        <FormField
          name='dental_nurse_bonus_rate'
          label='Bonus Rate - Dental Nurse (£/hr)'
          type='number'
          onChange={(e) => updateForm('dental_nurse_bonus_rate', e.target.value ? Number(e.target.value) : null)}
          value={form.dental_nurse_bonus_rate ?? undefined}
          errors={error?.dental_nurse_bonus_rate ?? []}
          helperText={`Override bonus rate for this job. If unspecified, practice's default ${defaultBonusRateLabel} will apply.`}
        />
      ) : null}
      {canRequestSpecialist ? (
        <React.Fragment>
          <Typography variant='h4'>Specialist Requirements</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
            <SwitchField 
              label='Sedation'
              onChange={(checked) => updateForm('sedation_required', checked)}
              value={form.sedation_required ?? false}
            />
            <SwitchField 
              label='Radiography'
              onChange={(checked) => updateForm('radiography_required', checked)}
              value={form.radiography_required ?? false}
            />
            <SwitchField 
              label='Implant'
              onChange={(checked) => updateForm('implant_required', checked)}
              value={form.implant_required ?? false}
            />
            <SwitchField 
              label='Orthodontics'
              onChange={(checked) => updateForm('orthodontics_required', checked)}
              value={form.orthodontics_required ?? false}
            />
          </Box>
        </React.Fragment>
      ) : null}
      { canSelectNursePreference ? (
        <React.Fragment>
          <FormField
            name='nurse_preference'
            label='Nurse Preference'
            select
            onChange={(e) => updateNursePreference(e.target.value as NursePreference)}
            value={form.nurse_preference ?? 'any'}
          >
            {nursePreferenceOption?.available_options.map((option) => (
              <MenuItem key={option} value={option}>{NURSE_PREFERENCE_LABEL_MAPPING[option]}</MenuItem>
            ))}
          </FormField>
          {form.nurse_preference === 'preferred_first' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
              <DateTimePicker
                name='preferred_nurses_wait_until'
                label='How long shall we wait for a preferred nurse, before opening the job to other nurses?'
                onChange={(value) => updateForm('preferred_nurses_wait_until', value?.toISO() ?? '')}
                value={form.preferred_nurses_wait_until ? DateTime.fromISO(form.preferred_nurses_wait_until) : null}
                maxDateTime={maxWaitUntil}
                disablePast
                timezone={practice?.tz ?? 'Europe/London'}
              />
              { waitUntilErrorMessage ? (
                <Typography variant='caption' color='error'>
                  {waitUntilErrorMessage}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </React.Fragment>
      ) : null}
     {practice?.practice_id && canAddPreferredNurses ? (
        <Typography variant='caption'>
          Enjoy working with a particular nurse? <br />
          <Link component={RouterLink} to={practicePreferredNursesURL(practice.practice_id)}>Click here</Link> 
          {' '} to add them to your preferred nurses list.
        </Typography>
      ) : null}
      {/* <Link color='secondary' onClick={() => openZendesk()} sx={{ cursor: 'pointer' }} >
        If you want to request a specific nurse, click here to chat to us, we can arrange that for you.
      </Link> */}
      {error?.schema ? (
        <Alert severity='error' variant='filled'><ErrorList errors={error.schema} /></Alert>
      ) : null}
      { paygradeStats && estimatedCost ? (
        <Alert severity='info' variant='filled'>
          <Typography>Expected total price: <strong>£{round(estimatedCost.median_total, 2)} per day</strong> ({round(estimatedCost.billable_hours, 1)} hours total)</Typography>
          {estimatedCost.discount ? (
            <Typography>{estimatedCost.discount}</Typography>
          ) : null}
          {/* <Typography variant='caption'>
            Our top priority is to fill this position for you. <br />
            If the process takes longer than expected, we may provide bonus rates (£{DENTAL_NURSE_BONUS_RATE}/hr) to nurses to help ensure your job is filled.
          </Typography> */}
        </Alert>
      ) : null }
    </React.Fragment>
  )
}

export default JobForm